import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Undo2, History, Filter, CheckCircle2, XCircle, Clock, Lock } from "lucide-react";
import { api } from "../lib/api";

/**
 * Segretaria IA · Cronologia + Rollback.
 * Route: /settings/cronologia-segretaria
 */
export default function CronologiaSegretaria() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await api.chatbotListActions({ limit: 100, ...(filter ? { status: filter } : {}) }));
    } catch { toast.error("Impossibile caricare la cronologia"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [filter]);

  const doUndo = async (a) => {
    if (!a.can_undo_now) return;
    if (!window.confirm("Annullare questa azione? L'operazione verrà riportata allo stato precedente.")) return;
    setBusyId(a.id);
    try {
      await api.chatbotUndoAction(a.id);
      toast.success("Azione annullata");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile annullare");
    } finally { setBusyId(null); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="page-cronologia-segretaria">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <History size={22} style={{ color: "var(--pxos-accent)" }}/>
            Cronologia Segretaria IA
          </h1>
          <p className="text-sm opacity-70 mt-1">
            Tutte le azioni proposte, eseguite, annullate. Puoi tornare indietro entro 24h
            dall'esecuzione (email escluse — non annullabili).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="opacity-60"/>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
                  className="pxos-input text-xs" data-testid="cronologia-filter">
            <option value="">Tutti gli stati</option>
            <option value="proposed">Proposte</option>
            <option value="executed">Eseguite</option>
            <option value="rejected">Rifiutate</option>
            <option value="undone">Annullate</option>
            <option value="failed">Fallite</option>
          </select>
        </div>
      </header>

      {loading ? (
        <div className="opacity-70 text-sm" data-testid="cronologia-loading">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="pxos-card p-6 text-center opacity-70 text-sm" data-testid="cronologia-empty">
          Nessuna azione in cronologia.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="cronologia-list">
          {items.map((a) => (
            <li key={a.id} className="pxos-card p-3 flex items-start gap-3"
                data-testid={`cronologia-item-${a.id}`}>
              <StatusIcon status={a.status} requires_stepup={a.requires_stepup}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  <span className="font-semibold">{prettyAction(a.action_type)}</span>
                  <span className="text-[10px] uppercase tracking-widest opacity-60">{a.status}</span>
                  {a.requires_stepup && <span className="text-[10px] flex items-center gap-0.5 opacity-70"><Lock size={10}/> L2</span>}
                </div>
                <div className="text-xs opacity-80 mt-1 break-words">{describe(a)}</div>
                <div className="text-[10px] opacity-50 mt-1">
                  Proposta: {new Date(a.proposed_at).toLocaleString("it-IT")}
                  {a.executed_at && <> · Eseguita: {new Date(a.executed_at).toLocaleString("it-IT")}</>}
                  {a.undone_at && <> · Annullata: {new Date(a.undone_at).toLocaleString("it-IT")}</>}
                </div>
                {a.error_message && (
                  <div className="text-[11px] mt-1" style={{ color: "#B42318" }}>Errore: {a.error_message}</div>
                )}
              </div>
              {a.status === "executed" && a.can_undo_now && (
                <button onClick={() => doUndo(a)}
                        disabled={busyId === a.id}
                        className="pxos-btn text-xs"
                        data-testid={`cronologia-undo-${a.id}`}
                        title="Annulla azione entro 24h">
                  <Undo2 size={12}/> Annulla
                </button>
              )}
              {a.status === "executed" && !a.can_undo_now && a.action_type === "send_accountant_email" && (
                <span className="text-[10px] opacity-60 max-w-[120px] text-right">
                  Email inviata,<br/>non annullabile
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusIcon({ status, requires_stepup }) {
  const size = 18;
  if (status === "executed") return <CheckCircle2 size={size} className="mt-0.5" style={{ color: "#059669" }}/>;
  if (status === "rejected") return <XCircle size={size} className="mt-0.5 opacity-60"/>;
  if (status === "undone") return <Undo2 size={size} className="mt-0.5 opacity-70"/>;
  if (status === "failed") return <XCircle size={size} className="mt-0.5" style={{ color: "#B42318" }}/>;
  return <Clock size={size} className="mt-0.5" style={{ color: requires_stepup ? "#B45309" : "var(--pxos-accent)" }}/>;
}

function prettyAction(t) {
  return {
    create_cycle: "Crea ciclo",
    create_deadline: "Crea scadenza",
    create_event: "Crea evento",
    query_cycles: "Ricerca cicli",
    create_personal_goal: "Obiettivo personale",
    create_financial_item: "Voce finanziaria",
    query_finances: "Ricerca finanze",
    move_cycle: "Sposta ciclo",
    delete_cycle: "Elimina ciclo",
    delete_financial_item: "Elimina voce",
    mark_financial_paid: "Segna pagata",
    send_accountant_email: "Invio commercialista",
    link_document_finance: "Collega documento",
    create_reminder: "Promemoria",
    multi_action: "Multi-azione",
    add_memory: "Memoria",
    other: "Altro",
  }[t] || t;
}

function describe(a) {
  const p = a.parameters || {};
  const t = a.action_type;
  if (t === "create_cycle" || t === "create_deadline" || t === "create_event" || t === "create_reminder") return p.title || "(senza titolo)";
  if (t === "move_cycle") return `${p._task_title || ""} → ${p.new_date || ""}${p.new_time ? " " + p.new_time : ""}`;
  if (t === "delete_cycle") return p._task_title || "(sconosciuto)";
  if (t === "delete_financial_item") return p._item_name || "(sconosciuto)";
  if (t === "mark_financial_paid") return `${p._item_name || "?"} · € ${p.amount_actual ?? "?"}`;
  if (t === "send_accountant_email") return `Anno fiscale ${p.fiscal_year || "?"}${p.period_hint ? " · " + p.period_hint : ""}`;
  if (t === "create_financial_item") return `${p.name || "?"}${p.amount_expected ? " · € " + p.amount_expected : ""}`;
  if (t === "multi_action") return `${(p.cycles || []).length} cicli in batch`;
  if (t === "add_memory") return (p.content || "").slice(0, 120);
  if (t === "link_document_finance") return "Documento → voce finanziaria";
  return "";
}
